/**
 * Panneau d'exclusion des vrais absents des sessions passées.
 * L'utilisateur charge la liste des étudiants absents à la fois en ordinaire
 * ET en rattrapage du semestre impair précédent, puis coche ceux qu'il veut
 * marquer « absent confirmé » (à exclure des brassages futurs) ou « réintégré »
 * (à inclure). La décision est persistée sur Etudiant.statut_absentiste.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { UserX, Loader2, Save, Search, RefreshCw, Info, CheckSquare, Square } from 'lucide-react';
import apiClient from '../../config/api.config';

interface EtudiantAbsent {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  niveau: string;
  classe: string;
  pole: string;
  option: string;
  statut_absentiste: '' | 'absent_confirme' | 'reintegre';
  statut_absentiste_date?: string | null;
  statut_absentiste_par?: string | null;
}

interface Reponse {
  annee: string;
  semestre: number;
  session_ordinaire: { id: number; nom: string } | null;
  session_rattrapage: { id: number; nom: string } | null;
  etudiants: EtudiantAbsent[];
  diagnostic: { nb_etudiants_evalues: number; nb_vrais_absents: number };
}

interface Props {
  anneeCourante: string;
}

export const ExclusionAbsentsPanel: React.FC<Props> = ({ anneeCourante }) => {
  const [expanded, setExpanded] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [donnees, setDonnees] = useState<Reponse | null>(null);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [filtre, setFiltre] = useState('');

  const charger = async () => {
    setChargement(true);
    setErreur(null);
    setSucces(null);
    try {
      const params = new URLSearchParams();
      if (anneeCourante) params.set('annee', anneeCourante);
      params.set('semestre', '1');
      const r = await apiClient.get<Reponse>(
        `/brassage/vrais-absents-semestre-passe/?${params.toString()}`
      );
      setDonnees(r.data);
      const initial: Record<string, boolean> = {};
      r.data.etudiants.forEach((e) => {
        initial[e.matricule] = e.statut_absentiste !== 'reintegre';
      });
      setSelection(initial);
    } catch (e: any) {
      setErreur(e?.response?.data?.error || 'Chargement échoué');
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    if (expanded && !donnees) charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const filtered = useMemo(() => {
    if (!donnees) return [];
    const q = filtre.trim().toLowerCase();
    if (!q) return donnees.etudiants;
    return donnees.etudiants.filter(
      (e) =>
        e.matricule.toLowerCase().includes(q) ||
        e.nom.toLowerCase().includes(q) ||
        e.prenoms.toLowerCase().includes(q) ||
        (e.classe || '').toLowerCase().includes(q)
    );
  }, [donnees, filtre]);

  const nbCoches = Object.values(selection).filter(Boolean).length;

  const enregistrer = async () => {
    if (!donnees) return;
    setEnregistrement(true);
    setErreur(null);
    setSucces(null);
    try {
      const cochés = donnees.etudiants
        .filter((e) => selection[e.matricule])
        .map((e) => e.matricule);
      const decochés = donnees.etudiants
        .filter((e) => !selection[e.matricule])
        .map((e) => e.matricule);
      if (cochés.length > 0) {
        await apiClient.post('/brassage/marquer-statut-absentiste/', {
          matricules: cochés,
          statut: 'absent_confirme',
        });
      }
      if (decochés.length > 0) {
        await apiClient.post('/brassage/marquer-statut-absentiste/', {
          matricules: decochés,
          statut: 'reintegre',
        });
      }
      setSucces(
        `Décisions enregistrées : ${cochés.length} exclus, ${decochés.length} réintégrés.`
      );
      await charger();
    } catch (e: any) {
      setErreur(e?.response?.data?.error || 'Enregistrement échoué');
    } finally {
      setEnregistrement(false);
    }
  };

  const toutCocher = () => {
    if (!donnees) return;
    const s: Record<string, boolean> = {};
    filtered.forEach((e) => (s[e.matricule] = true));
    setSelection({ ...selection, ...s });
  };
  const toutDecocher = () => {
    if (!donnees) return;
    const s: Record<string, boolean> = {};
    filtered.forEach((e) => (s[e.matricule] = false));
    setSelection({ ...selection, ...s });
  };

  return (
    <div className="rounded-lg border border-orange-200 dark:border-orange-800/60 bg-orange-50/50 dark:bg-orange-950/20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-orange-100/50 dark:hover:bg-orange-900/20 transition rounded-lg"
      >
        <UserX size={18} className="text-orange-500" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            Exclure les absents des sessions passées
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            Étudiants absents à la fois en ordinaire ET en rattrapage du semestre impair précédent
            {donnees && ` — ${donnees.diagnostic.nb_vrais_absents} détecté(s)`}
          </div>
        </div>
        <div className="text-xs px-2 py-1 rounded font-medium bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
          {expanded ? 'Masquer' : 'Afficher'}
        </div>
      </button>

      {expanded && (
        <div className="p-3 border-t border-orange-200 dark:border-orange-800/60 space-y-3">
          {donnees && (
            <div className="flex items-start gap-2 p-2 rounded text-xs bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
              <Info size={13} className="mt-0.5 text-blue-500 shrink-0" />
              <div>
                <div>
                  Référence : <strong>{donnees.session_ordinaire?.nom || '—'}</strong> +{' '}
                  <strong>{donnees.session_rattrapage?.nom || '—'}</strong>
                </div>
                <div className="text-[11px] mt-0.5 text-gray-500">
                  {donnees.diagnostic.nb_etudiants_evalues} étudiants évalués,{' '}
                  {donnees.diagnostic.nb_vrais_absents} sans aucune copie sur les 2 sessions.
                </div>
              </div>
            </div>
          )}

          {erreur && (
            <div className="p-2 rounded bg-red-100 text-red-800 text-xs border border-red-200">
              {erreur}
            </div>
          )}
          {succes && (
            <div className="p-2 rounded bg-green-100 text-green-800 text-xs border border-green-200">
              {succes}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={charger}
              disabled={chargement}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded font-medium disabled:opacity-50 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600"
            >
              {chargement ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Recharger
            </button>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filtre}
                onChange={(e) => setFiltre(e.target.value)}
                placeholder="Filtrer nom / matricule / classe"
                className="pl-7 pr-3 py-1.5 text-xs rounded border w-64 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              />
            </div>
            <button
              onClick={toutCocher}
              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
              disabled={!donnees}
            >
              <CheckSquare size={12} /> Tout cocher
            </button>
            <button
              onClick={toutDecocher}
              className="text-xs text-gray-600 hover:underline inline-flex items-center gap-1"
              disabled={!donnees}
            >
              <Square size={12} /> Tout décocher
            </button>
            <span className="ml-auto text-xs font-medium">
              {nbCoches} coché(s) → à exclure
            </span>
            <button
              onClick={enregistrer}
              disabled={enregistrement || !donnees}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded font-medium disabled:opacity-50"
            >
              {enregistrement ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Enregistrer les décisions
            </button>
          </div>

          {chargement && !donnees ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              <Loader2 size={16} className="inline animate-spin mr-2" />
              Chargement des vrais absents…
            </div>
          ) : donnees && donnees.etudiants.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-600 dark:text-gray-400">
              Aucun vrai absent détecté pour {donnees.annee} — S{donnees.semestre}.
            </div>
          ) : donnees ? (
            <div className="border rounded overflow-hidden border-gray-200 dark:border-gray-700">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-10"></th>
                      <th className="px-2 py-1.5 text-left">Matricule</th>
                      <th className="px-2 py-1.5 text-left">Nom & Prénoms</th>
                      <th className="px-2 py-1.5 text-left">Classe</th>
                      <th className="px-2 py-1.5 text-left">Pôle</th>
                      <th className="px-2 py-1.5 text-left">Décision actuelle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e) => {
                      const coche = !!selection[e.matricule];
                      return (
                        <tr key={e.matricule}
                          className={`border-t border-gray-100 dark:border-gray-800 ${
                            coche ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                          }`}
                        >
                          <td className="px-2 py-1.5">
                            <input
                              type="checkbox"
                              checked={coche}
                              onChange={(ev) =>
                                setSelection({ ...selection, [e.matricule]: ev.target.checked })
                              }
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono">{e.matricule}</td>
                          <td className="px-2 py-1.5">{e.prenoms} {e.nom}</td>
                          <td className="px-2 py-1.5">{e.classe}</td>
                          <td className="px-2 py-1.5">{e.pole}</td>
                          <td className="px-2 py-1.5">
                            {e.statut_absentiste === 'absent_confirme' ? (
                              <span className="text-orange-600 font-medium">Absent confirmé</span>
                            ) : e.statut_absentiste === 'reintegre' ? (
                              <span className="text-green-600 font-medium">Réintégré</span>
                            ) : (
                              <span className="text-gray-500">Non renseigné</span>
                            )}
                            {e.statut_absentiste_par && (
                              <div className="text-[10px] text-gray-500">
                                {e.statut_absentiste_par}
                                {e.statut_absentiste_date && ` · ${new Date(e.statut_absentiste_date).toLocaleDateString('fr-FR')}`}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
